import { useMemo, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import request from 'lib/request';
import {
  wellCacheAtom,
  unitCacheAtom,
  fetchWellAtom,
  fetchUnitAtom,
  mapSpatialFilterAtom,
  aoiPolygonAtom,
} from './atoms';
import type {
  ComparisonRequestPayload,
  ComparisonResult,
  EvalVersion,
  MatchStatus,
  WellQueryResult,
  UnitVersionResponse,
} from './types';

const VALID_EVAL_VERSIONS: EvalVersion[] = ['eval_2', 'eval_3'];

/**
 * Parses and validates comparison parameters from the current URL search string.
 * Returns null if required params are missing or invalid.
 * @return {ComparisonRequestPayload | null} Parsed request payload, or null if invalid.
 */
export const useComparisonParams = (): ComparisonRequestPayload | null => {
  const { search } = useLocation();
  return useMemo(() => {
    const p = new URLSearchParams(search);

    const parseInteger = (key: string): number | null => {
      const raw = p.get(key);
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isInteger(n) ? n : null;
    };

    const parseFloatInRange = (
      key: string,
      min: number,
      max: number
    ): number | null => {
      const raw = p.get(key);
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isFinite(n) && n >= min && n <= max ? n : null;
    };

    const sideAVersion = p.get('side_a_eval_version') as EvalVersion;
    const sideBVersion = p.get('side_b_eval_version') as EvalVersion;
    if (
      !VALID_EVAL_VERSIONS.includes(sideAVersion) ||
      !VALID_EVAL_VERSIONS.includes(sideBVersion)
    )
      return null;

    const sideAEvalId = parseInteger('side_a_evaluation_id');
    const sideBEvalId = parseInteger('side_b_evaluation_id');
    const threshold = parseFloatInRange('variance_threshold', 0, 1);

    if (sideAEvalId === null) return null;
    if (sideBEvalId === null) return null;
    if (threshold === null) return null;

    return {
      side_a: {
        eval_version: sideAVersion,
        evaluation_id: sideAEvalId,
      },
      side_b: {
        eval_version: sideBVersion,
        evaluation_id: sideBEvalId,
      },
      variance_threshold: threshold,
    };
  }, [search]);
};

// ── Geometry hooks ──

interface SlotGeometry {
  well: WellQueryResult | null;
  unit: UnitVersionResponse | null;
  loading: boolean;
}

/**
 * Fetches and caches geometry data for a slot (well + unit). Uses
 * mutationAtomWithLoadable atoms for fetching, with primitive atom caches for
 * persistence across mount/unmount cycles. The subtract geometry is not fetched
 * here — the property mini-maps, this hook's only consumer, do not draw it.
 * @param {string | null} uwi - Well UWI
 * @param {string} devAreaName - Development area name
 * @return {SlotGeometry} Geometry data and loading state
 */
export const useSlotGeometry = (
  uwi: string | null,
  devAreaName: string | null
): SlotGeometry => {
  const wellCache = useAtomValue(wellCacheAtom);
  const unitCache = useAtomValue(unitCacheAtom);

  const triggerWell = useSetAtom(fetchWellAtom);
  const triggerUnit = useSetAtom(fetchUnitAtom);

  const cachedWell = uwi ? (wellCache[uwi] ?? null) : null;
  const cachedUnit = devAreaName ? (unitCache[devAreaName] ?? null) : null;

  useEffect(() => {
    if (uwi && !cachedWell) triggerWell(uwi);
    if (devAreaName && !cachedUnit) triggerUnit(devAreaName);
  }, [uwi, devAreaName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading while any requested resource is still unresolved: a well (when a uwi
  // is present) or the unit geometry (when a dev area is present). Per resource,
  // so the uwi-only and dev-area-only cases still surface the spinner.
  const loading =
    (uwi !== null && cachedWell === null) ||
    (devAreaName !== null && cachedUnit === null);

  return {
    well: cachedWell,
    unit: cachedUnit,
    loading,
  };
};

/** A comparison slot's well geometry tagged with its Origin/Target membership. */
export interface ComparisonWell {
  uwi: string;
  matchStatus: MatchStatus;
  well: WellQueryResult;
}

interface ComparisonWellsResult {
  /** Loaded wells (a subset of the requested set as fetches resolve). */
  wells: ComparisonWell[];
  /** Distinct wells (by uwi) present across the comparison results. */
  totalWithUwi: number;
  /** Distinct wells actually requested (capped for performance). */
  requestedCount: number;
}

// Cap the number of distinct wells fetched for the section map so a large
// comparison doesn't fan out into hundreds of /queries/wells requests.
const MAX_MAP_WELLS = 100;

/**
 * Loads well geometry for every comparison slot that has a UWI, tagging each
 * with its Origin/Target membership (matchStatus). Reuses the per-uwi well
 * cache; fetches are triggered once per uwi and resolve progressively.
 * @param {ComparisonResult[]} results - The comparison slot results
 * @return {ComparisonWellsResult} Loaded wells + coverage counts
 */
export const useComparisonWells = (
  results: ComparisonResult[]
): ComparisonWellsResult => {
  const wellCache = useAtomValue(wellCacheAtom);
  const triggerWell = useSetAtom(fetchWellAtom);
  const triggeredRef = useRef<Set<string>>(new Set());

  // Distinct uwi → matchStatus (first occurrence wins), capped.
  const requested = useMemo(() => {
    const seen = new Map<string, MatchStatus>();
    results.forEach((r) => {
      if (r.uwi && !seen.has(r.uwi)) seen.set(r.uwi, r.matchStatus);
    });
    return Array.from(seen.entries())
      .slice(0, MAX_MAP_WELLS)
      .map(([uwi, matchStatus]) => ({ uwi, matchStatus }));
  }, [results]);

  useEffect(() => {
    requested.forEach(({ uwi }) => {
      if (!wellCache[uwi] && !triggeredRef.current.has(uwi)) {
        triggeredRef.current.add(uwi);
        triggerWell(uwi);
      }
    });
  }, [requested, wellCache, triggerWell]);

  const wells = useMemo(
    () =>
      requested
        .map(({ uwi, matchStatus }) => {
          const well = wellCache[uwi];
          return well ? { uwi, matchStatus, well } : null;
        })
        .filter((w): w is ComparisonWell => w !== null),
    [requested, wellCache]
  );

  const totalWithUwi = useMemo(() => {
    const set = new Set<string>();
    results.forEach((r) => {
      if (r.uwi) set.add(r.uwi);
    });
    return set.size;
  }, [results]);

  return { wells, totalWithUwi, requestedCount: requested.length };
};

/** A development-area unit boundary drawn as map context. */
export interface ComparisonUnit {
  name: string;
  unit: UnitVersionResponse;
}

// Distinct dev areas in a comparison are few, but cap defensively.
const MAX_MAP_UNITS = 50;

/**
 * Loads development-area unit geometry for the comparison's distinct dev areas,
 * for drawing neutral boundary context under the wells. Reuses the per-dev-area
 * unit cache; each area is fetched once.
 * @param {ComparisonResult[]} results - The comparison slot results
 * @return {ComparisonUnit[]} Loaded unit boundaries
 */
export const useComparisonUnits = (
  results: ComparisonResult[]
): ComparisonUnit[] => {
  const unitCache = useAtomValue(unitCacheAtom);
  const triggerUnit = useSetAtom(fetchUnitAtom);
  const triggeredRef = useRef<Set<string>>(new Set());

  const names = useMemo(() => {
    const set = new Set<string>();
    results.forEach((r) => {
      if (r.developmentAreaName) set.add(r.developmentAreaName);
    });
    return Array.from(set).slice(0, MAX_MAP_UNITS);
  }, [results]);

  useEffect(() => {
    names.forEach((name) => {
      if (!unitCache[name] && !triggeredRef.current.has(name)) {
        triggeredRef.current.add(name);
        triggerUnit(name);
      }
    });
  }, [names, unitCache, triggerUnit]);

  return useMemo(
    () =>
      names
        .map((name) => {
          const unit = unitCache[name];
          return unit ? { name, unit } : null;
        })
        .filter((u): u is ComparisonUnit => u !== null),
    [names, unitCache]
  );
};

/** A single entry in the wells query's `filteredUwis` aggregation. */
interface FilteredUwiEntry {
  uwi: string;
}

/**
 * Resolves the set of the comparison's UWIs that fall inside the drawn AOI,
 * using the same backend spatial query the Type Curve page uses: POST
 * /queries/wells with the polygon plus the comparison's UWIs, reading back the
 * `filteredUwis` aggregation (the full in-polygon set, not the paginated rows).
 * Unlike a client-side point-in-polygon test this has no dependency on loaded
 * map geometry and no well-count cap. Returns `null` when no AOI is active (→ no
 * scoping) or on request failure (fail-open: show everything rather than hide
 * every property).
 * @param {ComparisonResult[]} results - The comparison slot results
 * @return {object} `{ uwis }` — the in-AOI UWI set (or null) — plus `loading`
 */
export const useAoiFilteredUwis = (
  results: ComparisonResult[]
): { uwis: Set<string> | null; loading: boolean } => {
  const spatialFilter = useAtomValue(mapSpatialFilterAtom);
  const aoiPolygon = useAtomValue(aoiPolygonAtom);

  const uwiIds = useMemo(() => {
    const set = new Set<string>();
    results.forEach((r) => {
      if (r.uwi) set.add(r.uwi);
    });
    return Array.from(set);
  }, [results]);

  const [uwis, setUwis] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(false);

  // Refetch only when the polygon or the comparison's UWI set actually changes;
  // a null signature means no AOI is active (no query, no scoping).
  const signature =
    spatialFilter === 'aoi' && aoiPolygon && uwiIds.length > 0
      ? JSON.stringify({ p: aoiPolygon.coordinates, u: uwiIds })
      : null;

  useEffect(() => {
    if (!signature || !aoiPolygon) {
      setUwis(null);
      setLoading(false);
      return undefined;
    }
    const abortController = new AbortController();
    let cancelled = false;
    setLoading(true);
    request
      .post<{ filteredUwis?: FilteredUwiEntry[] }, unknown>(
        '/queries/wells',
        {
          query: {
            polygonGeojson: {
              type: aoiPolygon.type,
              coordinates: aoiPolygon.coordinates,
            },
            uwiIds,
            useElastic: true,
            includeFilteredUwis: true,
            page: 1,
            pageSize: 1,
            limit: 1,
          },
        },
        abortController
      )
      .then((response) => {
        if (cancelled || response.type === 'abort') return;
        // Fail-open on error: leave scoping off rather than hiding every row.
        setUwis(
          response.type === 'success'
            ? new Set((response.data.filteredUwis ?? []).map((w) => w.uwi))
            : null
        );
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setUwis(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
      abortController.abort();
    };
    // `signature` encodes the polygon + uwiIds this request depends on.
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  return { uwis, loading };
};
